import { useState, useEffect } from "react";
import {
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Card,
  Typography,
  Row,
  Col,
} from "antd";

import moment from "moment";

import { TransactionParameters } from "../types";
import { validateEmail, validateParameters } from "../utils/validators";
import { getDefaultDates } from "../utils/helpers";
import CurrencyInput from "./CurrencyInput";

const { Title, Paragraph } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface ParametersFormProps {
  onGenerate: (params: TransactionParameters, email: string) => void;
  loading: boolean;
}

const ParametersForm: React.FC<ParametersFormProps> = ({
  onGenerate,
  loading,
}) => {
  const [form] = Form.useForm();
  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [count, setCount] = useState<number>(5);

  // Parse URL parameters and set email if provided
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const emailParam = searchParams.get("email");

    if (emailParam) {
      setEmail(emailParam);
      // Validate the email from URL
      const validation = validateEmail(emailParam);
      if (!validation.valid) {
        setEmailError(validation.error!);
      } else {
        setEmailError(null);
      }
    }
  }, []);

  // Set default dates when component mounts
  useEffect(() => {
    const { startDate, endDate } = getDefaultDates();
    form.setFieldsValue({
      dateRange: [moment(startDate), moment(endDate)],
      amountMin: 10,
      amountMax: 1000,
      transactionType: "both",
    });
  }, [form]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    const validation = validateEmail(value);
    if (!validation.valid) {
      setEmailError(validation.error!);
    } else {
      setEmailError(null);
    }
  };

  const handleSubmit = () => {
    // Validate email first
    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      setEmailError(emailValidation.error!);
      return;
    }

    // Then validate and get form values
    form.validateFields().then((values) => {
      const params: TransactionParameters = {
        amountMin: values.amountMin,
        amountMax: values.amountMax,
        transactionType: values.transactionType,
        bookingDateStart: values.dateRange[0].format("YYYY-MM-DD"),
        bookingDateEnd: values.dateRange[1].format("YYYY-MM-DD"),
        purposePrefix: values.purposePrefix || "",
        senderNameTemplate: values.senderNameTemplate || "",
        ibanPrefix: values.ibanPrefix || "",
        count: count,
      };

      // Validate parameters
      const validation = validateParameters(params);
      if (!validation.valid) {
        // This should be caught by form validation, but just in case
        return;
      }

      onGenerate(params, email);
    });
  };

  return (
    <Card>
      <Title level={3}>Bank Transaction Generator</Title>
      <Paragraph>
        Generate test bank transactions for the sandbox environment.
      </Paragraph>

      <Form
        onFinish={handleSubmit}
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        <Form.Item
          label="Customer Email Address"
          validateStatus={emailError ? "error" : ""}
          help={emailError}
        >
          <Input
            placeholder="customer@example.com"
            value={email}
            onChange={handleEmailChange}
          />
        </Form.Item>

        <Row gutter={16} align="bottom" style={{ marginBottom: 16 }}>
          <Col span={16}>
            <Form.Item
              label="Number of Transactions to Generate"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={1}
                max={100}
                value={count}
                onChange={(value) => {
                  if (value !== null) setCount(value);
                }}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={8} style={{ textAlign: "right" }}>
            <Button type="primary" htmlType="submit" loading={loading}>
              Generate Transactions
            </Button>
          </Col>
        </Row>

        <Title level={4} style={{ marginTop: 24 }}>
          Transaction Parameters
        </Title>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="amountMin"
              label="Minimum Amount (EUR)"
              rules={[
                { required: true, message: "Please enter minimum amount" },
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null)
                      return Promise.resolve();
                    // No validation needed here since we'll check in amountMax
                    return Promise.resolve();
                  },
                },
              ]}
              dependencies={["amountMax"]}
            >
              <CurrencyInput
                min={0.01}
                step={0.01}
                style={{ width: "100%" }}
                placeholder="Max"
                onChange={() => form.validateFields(["amountMax"])}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="amountMax"
              label="Maximum Amount (EUR)"
              rules={[
                { required: true, message: "Please enter maximum amount" },
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null)
                      return Promise.resolve();

                    const minValue = form.getFieldValue("amountMin");
                    if (minValue && value <= minValue) {
                      return Promise.reject(
                        new Error(
                          "Maximum amount cannot be less than minimum amount"
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              dependencies={["amountMin"]}
            >
              <CurrencyInput
                min={0.01}
                step={0.01}
                style={{ width: "100%" }}
                placeholder="Max"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="transactionType"
          label="Transaction Type"
          rules={[
            { required: true, message: "Please select transaction type" },
          ]}
        >
          <Select>
            <Option value="both">Both Credit & Debit</Option>
            <Option value="credit">Credit Only</Option>
            <Option value="debit">Debit Only</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="dateRange"
          label="Payment Date Range"
          rules={[{ required: true, message: "Please select date range" }]}
        >
          <RangePicker style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item name="purposePrefix" label="Description (Optional)">
          <Input placeholder="e.g., 'Tax Payment -'" />
        </Form.Item>

        <Form.Item name="senderNameTemplate" label="Paid By (Optional)">
          <Input placeholder="e.g., 'Company Name'" />
        </Form.Item>

        <Form.Item name="ibanPrefix" label="IBAN Prefix (Optional)">
          <Input placeholder="e.g., 'DE'" />
        </Form.Item>
      </Form>
    </Card>
  );
};

export default ParametersForm;
